import { ClipboardCheck, SearchX, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EmptyIssuesStateProps {
  /**
   * True when the emptiness is the filter's doing rather than the project's.
   * The two cases need different words and different offers: one is "nothing
   * to do here", the other is "you have hidden it".
   */
  filtered: boolean;
  onClearFilters?: () => void;
  /**
   * The viewer sees only the issues they're on — a contributor. Unfiltered,
   * the project may well have issues, just none of theirs, and they can't
   * raise one. See [[empty-issues-state.spec]].
   */
  onlyYours?: boolean;
}

export function EmptyIssuesState({
  filtered,
  onClearFilters,
  onlyYours = false,
}: EmptyIssuesStateProps) {
  const Icon = filtered ? SearchX : onlyYours ? UserRound : ClipboardCheck;
  const heading = filtered
    ? "Nothing matches"
    : onlyYours
      ? "No issues for you here"
      : "No issues yet";
  const message = filtered
    ? "No issues match the filters you've applied."
    : onlyYours
      ? "Issues you're added to will show up here."
      : "Issues are raised from a photo. Open one from Photos and raise an issue against it.";

  return (
    <div className="flex h-full w-full items-center justify-center px-10 py-12">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <Icon className="h-12 w-12 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">{heading}</h3>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        {filtered && onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
